import React, { ComponentType, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '@app/core/i18n';

import { Input } from '@ast/magma/components/input';

import { InputAttributes } from 'react-number-format/types/types';

import { FieldBehaviorMode } from '@app/queryTyping';

import { MaskedNumberInput, MaskedNumberInputValues } from '@app/common/components/MaskedNumberInput/MaskedNumberInput';
import {
  Field,
  FieldComponent,
  FieldLocator,
  isSsnField,
} from '@app/common/configurable-wizards';
import { combineNames, getFieldErrorMessage } from '@app/common/configurable-wizards/utils';
import { isString } from '@app/common/utils';
import { useCommonValidationMessages } from '@app/common/valiation/commonValidationMessages';

import { SsnFieldFragment as SsnField } from '../../queries/queryTyping/all-fields';

import { MASK_CHAR, MASK_PATTERN_CHAR, PATTERN_CHAR } from './const';
import { SSNFieldComponent } from './SSNInput';
import { useNormilizePattern } from './useNormilizePattern';
import { fieldLengthWithoutHypen } from './utils';

export const PersonalSSNFieldComponent: FieldComponent<SsnField> = (props) => {
  const { t } = useTranslation();
  const {
    field,
    parent,
  } = props;

  const name = combineNames(parent, field.id);
  const { formState: { errors } } = useFormContext();
  const commonValidationMessages = useCommonValidationMessages();
  const error = getFieldErrorMessage(name, errors, commonValidationMessages);
  const fieldLength = fieldLengthWithoutHypen(field.dataPattern);
  const is4Length = () => fieldLength === 4;
  const is9Length = () => fieldLength === 9;

  const minLengthMessage = useMemo(() => {
    if (is4Length()) {
      return t(
        'personal-ssn.validation.minlength.lastfour|Validation message for last 4 digits configuration',
        'Please enter the last 4 digits of your SSN',
      );
    }

    if (is9Length()) {
      return t(
        'personal-ssn.validation.minlength.allnine|Validation message for all 9 digits configuration',
        'Please enter all 9 digits of your SSN',
      );
    }

    // eslint-disable-next-line i18next/no-literal-string
    throw new Error('Unxpected SSN length');
  }, [fieldLength]);

  const normilizedPattern = useNormilizePattern(field.dataPattern, field.dataMask);

  return (
    <SSNFieldComponent
      controllerProps={{
        render: (renderProps) => {
          const { onChange, ...renderFiled } = renderProps.field;
          const onValueChange = ({ value, formattedValue }: MaskedNumberInputValues) => {
            if (is4Length()) {
              // if 4-length mode, keep only last 4 digits
              onChange(value);
            } else if (is9Length()) {
              // else keep all 9 digits with hyphens
              onChange(formattedValue);
            }
          };

          return (
            <MaskedNumberInput
              customInput={Input as ComponentType<InputAttributes>}
              format={normilizedPattern}
              mask={field.dataMask!}
              patternChar={PATTERN_CHAR}
              maskPatternChar={MASK_PATTERN_CHAR}
              maskChar={MASK_CHAR}
              inputMode="tel"
              label={field.label}
              aria-invalid={!!error}
              onValueChange={onValueChange}
              data-stable-name={`${field.id}Input`}
              {...renderFiled}
            />
          );
        },
        rules: {
          required: field.fieldBehaviorMode === FieldBehaviorMode.REQUIRED,
          maxLength: {
            value: field.dataMask?.length || field.maxLength,
            message: '',
          },
          validate: {
            // eslint-disable-next-line consistent-return
            minLength: (val) => {
              if (isString(val)) {
                const trimmed = val.replace(/[^0-9-]/g, '');
                if (trimmed.length < field.minLength) {
                  return minLengthMessage;
                }
              }
            },
          },
        },
      }}
      name={name}
      error={error}
      {...props}
    />
  );
};

export const isPersonalSSNField = (field: Field) => (
  /**
   * @remark currently we is forced to use several ids to detect the same field.
   * It is because DS realization has to be refactored.
   * From wizard to wizard the same field may have the same ids.
   */
  isSsnField(field) && /Ssn|SocialSecurityNumber/i.test(field.id)
);

export const PersonalSSNLocator: FieldLocator = ({ field }) => (
  isPersonalSSNField(field)
    ? PersonalSSNFieldComponent
    : undefined
);
